import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormattedMessage from 'components/formatted-message';
import Loading from 'components/loading/loading';
import { actions as waiterActions } from 'redux/modules/waiter-of-process';
import { notificationTypes, notify } from 'redux/modules/notifications';
import * as endpoints from 'utils/api/endpoints';
import { VIP_CLIENT_IDS } from 'components/video-player/constants/video-player-configs';
import { apiBuilder } from 'utils/api/common-requests/common-requests';

import { DeleteIcon, MovieIcon } from '../svg-icons/playlist-icons';

import {
    WaiterLinkToFile,
    WaiterProgressRowLoadWrapper,
    WaiterProgressRowResetButton,
    WaiterProgressRowTitle,
    WaiterProgressRowTitleError,
    WaiterProgressRowWrapper,
    WaitingText
} from './waiter-of-process-styled-components';

const POLLING_INTERVAL_MS = 3000;

const statuses = {
    success: 'SUCCESS',
    progress: 'PROGRESS',
    pending: 'PENDING',
    failure: 'FAILURE'
};

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

const WITH_DEBUG = false;

// eslint-disable-next-line tpe/max-component-size -- TODO: legacy large component (baseline)
const WaiterProgressRowClipService = ({ childrenId, task, taskTitle, params, payload }) => {
    const dispatch = useDispatch();

    const [progressAtPercent, setProgressAtPercent] = useState(MIN_PERCENT);
    const [resultOfService, setResultOfService] = useState(null);
    const [statusServerError, setStatusServerError] = useState(false);
    const [statusServerProcessing, setStatusServerProcessing] = useState(false);
    const [success, setSuccess] = useState(false);

    const intervalRef = useRef(null);

    const isVip = useSelector(state => VIP_CLIENT_IDS.includes(state?.user?.profile?.clientId));

    const handleWaiter = useCallback(
        el => {
            dispatch(waiterActions.setPayload(el));
        },
        [dispatch]
    );

    const handleWaiterProcessing = useCallback(
        el => {
            dispatch(waiterActions.setProcessing(el));
        },
        [dispatch]
    );

    const onProcessingCallBack = useCallback(
        processingStatus => {
            handleWaiterProcessing({
                childrenId,
                processingStatus
            });
        },
        [handleWaiterProcessing, childrenId]
    );

    const handleStop = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return;
    }, []);

    const destroyCallBack = useCallback(() => {
        handleWaiter({
            childrenId,
            payload: null
        });

        handleWaiterProcessing({
            childrenId,
            processingStatus: false
        });

        handleStop();
    }, [handleWaiter, handleWaiterProcessing, childrenId, handleStop]);

    // eslint-disable-next-line tpe/async-in-effects -- TODO: legacy async callback (baseline)
    const fetchTaskStatus = useCallback(async () => {
        if (WITH_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('fetchTaskStatus: ', task);
        }

        if (!task) {
            return;
        }

        try {
            const url = endpoints.clipServiceTaskStatus(task, isVip);
            const response = await apiBuilder().get(url, { params, ...payload });

            const { state, progress, result } = response.data || {};

            if (WITH_DEBUG) {
                // eslint-disable-next-line no-console
                console.log('fetchTaskStatus result: ', task, state, progress, result);
            }

            const progressNumb = Number(progress);

            if (
                !state ||
                (state === statuses.success && progressNumb === MAX_PERCENT && !result) ||
                state === statuses.failure
            ) {
                setProgressAtPercent(MIN_PERCENT);
                dispatch(notify(<FormattedMessage id={'components.error.errorMessage'} />, notificationTypes.ERROR));
                setStatusServerProcessing(false);
                setStatusServerError(true);
                onProcessingCallBack?.(false);

                handleStop();
            }

            if (progressNumb > MIN_PERCENT) {
                setProgressAtPercent(progressNumb);
            }

            if (state === statuses.success && result) {
                setResultOfService(result);
                setSuccess(true);
                setStatusServerProcessing(false);

                handleStop();
            }

            if (state === statuses.progress || state === statuses.pending || progressNumb < MAX_PERCENT) {
                setStatusServerProcessing(true);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error fetching task status:', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review hook dependencies (baseline when the rule was enabled)
    }, [task, isVip, onProcessingCallBack]);

    useEffect(() => {
        if (!task) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            return;
        }

        if (WITH_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('pooling effect start: ', task);
        }

        fetchTaskStatus();

        intervalRef.current = setInterval(() => {
            if (WITH_DEBUG) {
                // eslint-disable-next-line no-console
                console.log('pooling effect start with interval: ', task);
            }

            fetchTaskStatus();
        }, POLLING_INTERVAL_MS);

        onProcessingCallBack?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review hook dependencies (baseline when the rule was enabled)
    }, [task]);

    useEffect(() => {
        if (!resultOfService || !success || !taskTitle) {
            return;
        }

        onProcessingCallBack?.(false);
    }, [resultOfService, taskTitle, success, onProcessingCallBack]);

    useEffect(() => {
        return () => {
            handleStop();
        };
    }, [handleStop]);

    return (
        <WaiterProgressRowWrapper>
            {/* eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline) */}
            <MovieIcon style={{ gridColumn: 2, gridRow: 1 }} />

            <WaiterProgressRowTitle errorOnTask={statusServerError}>{taskTitle}</WaiterProgressRowTitle>

            {!!statusServerError && (
                // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
                <WaiterProgressRowTitleError errorOnTask={true} style={{ gridColumn: 3 }}>
                    <FormattedMessage id="clipService.failure" />
                </WaiterProgressRowTitleError>
            )}

            {!statusServerError && progressAtPercent === MIN_PERCENT && !statusServerProcessing && (
                <WaiterProgressRowLoadWrapper>
                    <WaitingText>
                        <FormattedMessage id="clipService.waiting" />
                    </WaitingText>
                </WaiterProgressRowLoadWrapper>
            )}

            {!!statusServerProcessing && !statusServerError && (
                <WaiterProgressRowLoadWrapper>
                    <Loading spinner />
                    {/* eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline) */}
                    <span style={{ color: `var(--uiPrimaryColor)` }}>{` ${progressAtPercent} % `}</span>
                </WaiterProgressRowLoadWrapper>
            )}

            {!!success && Boolean(resultOfService) && !statusServerError && !statusServerProcessing && (
                <WaiterLinkToFile href={resultOfService} download={`${taskTitle.replaceAll(` `, `-`)}.mp4`}>
                    <FormattedMessage id="videoPlayer.playlists.subMenu.downloadEpisodes" />
                </WaiterLinkToFile>
            )}

            <WaiterProgressRowResetButton>
                <DeleteIcon flashOnFocus onClick={destroyCallBack} />
            </WaiterProgressRowResetButton>
        </WaiterProgressRowWrapper>
    );
};

export default WaiterProgressRowClipService;
